import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  DownloadSimple,
  CheckCircle,
  Clock,
  Info,
  SpinnerGap,
  Sparkle,
  ArrowLeft,
  Warning,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGES = {
  new: { label: 'تم الاستلام — قيد المراجعة', color: '#1F3D2B', icon: Clock },
  in_progress: { label: 'قيد التجهيز', color: '#1F3D2B', icon: SpinnerGap },
  needs_info: { label: 'بانتظار توضيحك', color: '#B45309', icon: Info },
  delivered: { label: 'تم التسليم ✅', color: '#4A641F', icon: CheckCircle },
};

const ClientDashboardPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/client/service-requests/${token}`)
      .then(async (r) => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(setData)
      .catch(() => setError('الطلب غير موجود أو الرابط منتهي'));
  }, [token]);

  if (error) {
    return (
      <main className="pt-24 min-h-screen bg-[#F7F5F2] flex items-center" dir="rtl">
        <div className="max-w-md mx-auto px-4 text-center">
          <Warning size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#1F3D2B] mb-3">{error}</h2>
          <Link to="/services/request">
            <Button className="bg-[#1F3D2B] text-white rounded-xl px-6 py-3">
              تقديم طلب جديد
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="pt-24 min-h-screen bg-[#F7F5F2] flex items-center justify-center">
        <SpinnerGap size={36} className="animate-spin text-[#1F3D2B]" />
      </main>
    );
  }

  const badge = STATUS_BADGES[data.status] || STATUS_BADGES.new;
  const BadgeIcon = badge.icon;

  return (
    <main
      data-testid="client-dashboard-page"
      className="pt-24 pb-16 min-h-screen bg-[#F7F5F2]"
      dir="rtl"
    >
      <div className="max-w-3xl mx-auto px-4 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-6 sm:p-8"
        >
          <p className="text-sm text-[#7A866E] mb-2">طلب رقم #{data.id.slice(0, 8)}</p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F3D2B] mb-3 font-['IBM_Plex_Sans_Arabic']">
            مرحباً {data.name} 🌿
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: `${badge.color}1A`, color: badge.color }}
            >
              <BadgeIcon
                size={16}
                weight="bold"
                className={data.status === 'in_progress' ? 'animate-spin' : ''}
              />
              {badge.label}
            </span>
            <span className="text-sm text-[#7A866E]">
              الخدمة: <strong className="text-[#1F3D2B]">{data.service_label}</strong>
            </span>
          </div>
        </motion.div>

        {/* Needs info banner */}
        {data.status === 'needs_info' && data.info_request_message && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-[20px] p-5"
          >
            <div className="flex items-start gap-3">
              <Info
                size={24}
                weight="duotone"
                className="text-amber-600 shrink-0 mt-0.5"
              />
              <div className="flex-1">
                <p className="font-bold text-amber-900 mb-2">
                  أثناء المراجعة، احتجنا بعض التفاصيل البسيطة عشان يكون التحليل أدق.
                </p>
                <p className="text-amber-900 leading-7 whitespace-pre-line">
                  {data.info_request_message}
                </p>
                <p className="text-sm text-amber-700 mt-3">
                  ردّي علينا على نفس البريد الإلكتروني الذي تواصلنا معك من خلاله.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Delivery */}
        {data.status === 'delivered' &&
          data.deliverable_files &&
          data.deliverable_files.length > 0 && (
            <motion.div
              data-testid="delivery-section"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-6 sm:p-8"
            >
              <h2 className="text-xl font-bold text-[#1F3D2B] mb-2 font-['IBM_Plex_Sans_Arabic']">
                جاهزين نسلّمك التقرير 📊
              </h2>
              {data.delivery_message && (
                <div className="bg-[#F5F2EC] border-r-4 border-[#4A641F] rounded-xl p-4 my-4">
                  <p className="text-[#1F3D2B] leading-8 whitespace-pre-line">
                    {data.delivery_message}
                  </p>
                </div>
              )}
              <div className="space-y-3 mt-4">
                {data.deliverable_files.map((f, idx) => (
                  <a
                    key={idx}
                    href={`${API_URL}${f.download_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`deliverable-download-${idx}`}
                    className="flex items-center justify-between p-4 bg-[#F5F2EC] hover:bg-[#EBE6DC] rounded-xl transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-lg bg-[#1F3D2B] flex items-center justify-center shrink-0">
                        <DownloadSimple size={20} className="text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-[#1F3D2B] truncate">
                          {f.filename}
                        </p>
                        <p className="text-xs text-[#7A866E]">
                          {Math.round(f.size / 1024)} KB
                        </p>
                      </div>
                    </div>
                    <span className="text-[#4A641F] font-bold text-sm shrink-0">
                      تحميل
                    </span>
                  </a>
                ))}
              </div>
            </motion.div>
          )}

        {/* Upsell */}
        {data.status === 'delivered' && data.next_step && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            data-testid="upsell-section"
            className="bg-gradient-to-l from-[#1F3D2B] to-[#3D512D] rounded-[24px] p-6 sm:p-8 text-white"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                <Sparkle size={22} weight="fill" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2 font-['IBM_Plex_Sans_Arabic']">
                  {data.service_type === 'diagnostic_review' ||
                  data.service_type === 'gap_analysis'
                    ? 'هل ترغب بخطة تحسين مخصصة؟'
                    : data.service_type === 'improvement_plan'
                    ? 'هل ترغب بمتابعة التنفيذ وقياس الأثر؟'
                    : 'الخطوة التالية'}
                </h3>
                <p className="text-[#D8E0C9] leading-8 mb-4">
                  ولو حاب نكمّل معك ونحوّل النتائج إلى خطوات عملية، نقدر نجهز لك
                  <strong className="text-white">
                    {' '}
                    {data.next_step.label_ar}{' '}
                  </strong>
                  مخصصة حسب أولوياتك.
                </p>
                <Link
                  to="/services/request"
                  data-testid="upsell-cta-btn"
                  className="inline-flex items-center gap-2 bg-white text-[#1F3D2B] hover:bg-[#F5F2EC] px-5 py-2.5 rounded-xl font-bold text-sm transition-colors"
                >
                  {data.service_type === 'diagnostic_review' ||
                  data.service_type === 'gap_analysis'
                    ? 'طلب خطة تحسين'
                    : 'طلب المتابعة'}
                  <ArrowLeft size={16} className="rtl-flip" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}

        {/* Pending state */}
        {data.status === 'new' && (
          <div className="bg-white rounded-[24px] shadow-sm border border-[#E6E0D6] p-6 text-center">
            <Clock size={32} className="text-[#4A641F] mx-auto mb-3" />
            <p className="text-[#1F3D2B] font-medium mb-1">
              طلبك بأيدٍ أمينة
            </p>
            <p className="text-[#7A866E] text-sm leading-7">
              بنراجع تفاصيلك ومرفقاتك ونرجع لك بخطوتنا التالية خلال يوم عمل.
            </p>
          </div>
        )}
      </div>
    </main>
  );
};

export default ClientDashboardPage;
